import { z } from "zod";

// Which evaluation a Powers view displays. Both instruments come from one PowersData / PersonProfile
// read: the self-assessment (`selfAssessmentResults` / `powersEvaluationResults`, the person's own rating)
// and the smart analysis (`smartAnalysisResults`, the AI reading of their documents). "combined" is the
// third lens, computed client-side - see combineLevels. Shared by the Power Map and the profile's Powers
// box so both mean the same thing by the same word.
export type ScoreSource = "self" | "smart" | "combined";

// The match-design value lists for the two power-score settings live HERE, not in the match codec,
// because Grow and Save read this module too and lib/ never imports from components/. The codec's
// RawDesign and the editor's MatchData both derive from these, so a value exists exactly once
// (spark#1068). Declared in lens order (see SCORE_SOURCES below).
export const PowerScoreLens = z.enum(["SELF_ASSESSMENT", "SMART_ANALYSIS", "COMBINED"]);
export type PowerScoreLens = z.infer<typeof PowerScoreLens>;
// Genesis' default, so a design without the key keeps computing as before the key existed.
export const DEFAULT_POWER_SCORE_LENS: PowerScoreLens = "COMBINED";

// Match settings and result metadata must name the same three lenses as the Power Map and profile.
// Keeping the message ids here makes that vocabulary structural rather than three matching copies.
export const POWER_SCORE_LENS_LABEL_IDS: Record<PowerScoreLens, string> = {
  SELF_ASSESSMENT: "grow.powerLibrary.sourceSelf",
  SMART_ANALYSIS: "grow.powerLibrary.sourceSmart",
  COMBINED: "grow.powerLibrary.sourceCombined",
};

// The lens order of every lens control and of the lens rows in the access overview, so a word sits at
// the same place wherever it appears.
export const SCORE_SOURCES = ["self", "smart", "combined"] as const satisfies readonly ScoreSource[];

export const SCORE_SOURCE_LENS: Record<ScoreSource, PowerScoreLens> = {
  self: "SELF_ASSESSMENT",
  smart: "SMART_ANALYSIS",
  combined: "COMBINED",
};

// The lenses a viewer may look through on a person's Powers box, one feature Permission each
// (save.profile.powers-source.<lens>), in lens order. None held: the combined score alone, the
// fail-closed view that tells nothing apart.
export function allowedScoreSources(holds: (source: ScoreSource) => boolean): ScoreSource[] {
  const allowed = SCORE_SOURCES.filter(holds);
  return allowed.length > 0 ? allowed : ["combined"];
}

export const MissingPowerScore = z.enum(["ZERO", "NEUTRAL"]);
export type MissingPowerScore = z.infer<typeof MissingPowerScore>;
export const DEFAULT_MISSING_POWER_SCORE: MissingPowerScore = "ZERO";

// Neben der Linse, weil beide dasselbe tun: den gespeicherten Wert auf das Wort abbilden, das die
// UI dafuer benutzt. Der Regler und die zugeklappte Zusammenfassung lesen dieselbe Zeile, damit
// die beiden Stellen nicht zwei Wortlaute fuer denselben Zustand entwickeln.
export const MISSING_POWER_LABEL_IDS: Record<MissingPowerScore, string> = {
  ZERO: "match.powerScore.missingZero",
  NEUTRAL: "match.powerScore.missingNeutral",
};

export type LevelMap = ReadonlyMap<string, number | null | undefined>;

// Combined ("gewichtet") = mean of the per-source scores that exist for a key (self + smart), rounded,
// mirroring genesis' CombinedScoreCalculator (it named fetchLatestAveragedScores until genesis#328 —
// same arithmetic, and the match's COMBINED lens is this lens). A key present in
// either map but scored in neither stays in the result as null, so "assessed nowhere" remains
// distinguishable from "absent". Computed from the two values callers already load; no extra backend call.
export function combineLevels(self: LevelMap, smart: LevelMap): Map<string, number | null> {
  const combined = new Map<string, number | null>();
  for (const key of new Set([...self.keys(), ...smart.keys()])) {
    const values = [self.get(key), smart.get(key)].filter((v): v is number => typeof v === "number");
    combined.set(
      key,
      values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null,
    );
  }
  return combined;
}
